// Design ref: lang-app-2026-07-13-jtalk-login (F/design-library)
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocialLogin } from '../hooks/useSocialLogin';
import SocialLoginOptions from '../components/SocialLoginOptions';

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, login, register } = useAuth();
  const social = useSocialLogin();
  const [emailOpen, setEmailOpen] = useState(false);
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [emailBusy, setBusy] = useState(false);
  const busy = emailBusy || social.busy;
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (user && !loading) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  if (user && !loading) {
    return null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'register' && !termsAccepted) {
      setError('続行するには利用規約（EULA）への同意が必要です');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else {
        await register({
          username: username.trim(),
          email: email.trim(),
          password,
          accepted_terms: termsAccepted,
        });
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || (mode === 'login' ? 'ログインに失敗しました' : '登録に失敗しました'));
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    social.setError('');
  };

  return (
    <div className="lg-page">
      <style>{CSS}</style>

      <div className="lg-inner">
        <header className="lg-hero">
          <div className="lg-wordmark">JTALK<span className="lg-brand-dot" /></div>
          <p className="lg-tagline">日本語で、世界とつながる。</p>
        </header>

        <section className="lg-card" aria-labelledby="lg-title">
          <div className="lg-form-heading">
            <div className="lg-heading-copy">
              <h1 id="lg-title" className="lg-title">{mode === 'login' ? 'おかえりなさい。' : 'はじめまして。'}</h1>
              <p className="lg-form-caption">{mode === 'login' ? 'ログイン方法を選んでください。' : '好きな方法ではじめましょう。'}</p>
            </div>
            <Kotomaru />
          </div>
          <div className="lg-tabs" role="group" aria-label="ログイン・新規登録の切り替え">
            <span className="lg-tab-indicator" style={{ transform: mode === 'login' ? 'translateX(0)' : 'translateX(100%)' }} />
            <button data-control="tab" data-control-id="Login-lg-tab" type="button" aria-pressed={mode === 'login'} className={`lg-tab ${mode === 'login' ? 'is-active' : ''}`} onClick={() => switchMode('login')} disabled={busy}>ログイン</button>
            <button data-control="tab" data-control-id="Login-lg-tab" type="button" aria-pressed={mode === 'register'} className={`lg-tab ${mode === 'register' ? 'is-active' : ''}`} onClick={() => { switchMode('register'); setEmailOpen(true); }} disabled={busy}>新規登録</button>
          </div>
          <SocialLoginOptions
            providers={social.providers} loaded={social.providersLoaded} busy={busy || loading}
            onSelect={provider => { setError(''); social.start(provider, termsAccepted); }}
            emailOpen={emailOpen} onToggleEmail={() => { setEmailOpen(value => !value); setError(''); social.setError(''); }}
          />
          {(mode === 'register' || !emailOpen || social.providers.some(provider => provider.enabled)) && <div className="lg-shared-consent">
            {/* Guideline 1.2: 新規登録と外部認証の前に EULA を提示し、明示的な同意を得る */}
            <label className="lg-terms-check">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span>
                <Link to="/terms" className="lg-eula-link">利用規約（EULA）</Link>
                と
                <a href="/privacy" className="lg-eula-link">プライバシーポリシー</a>
                を読み、同意します
              </span>
            </label>
            <p className="lg-eula">
              JTALK は嫌がらせ・不適切な投稿を一切許容しません。
            </p>

          </div>}
          {(error || social.error) && <p className="lg-error" role="alert">{error || social.error}</p>}
          {social.busy && <p className="lg-auth-status" role="status">ログインを確認しています…</p>}
          {social.linkRequest && <form className="lg-email-form lg-form" onSubmit={async event => {
            event.preventDefault();
            await social.confirmLink(linkPassword);
            setLinkPassword('');
          }}>
            <h2 className="lg-link-title">既存のアカウントと連携</h2>
            <p className="lg-form-caption">一度だけJTALKのパスワードを確認します。連携後は{social.linkRequest.provider}でログインできます。</p>
            <label className="lg-field">
              <span className="lg-label">JTALKのパスワード</span>
              <input className="lg-input" type="password" autoComplete="current-password" required autoFocus
                value={linkPassword} onChange={event => setLinkPassword(event.target.value)} disabled={busy} />
            </label>
            <button data-control="primary" data-control-id="Login-lg-submit" className="lg-submit" type="submit" disabled={busy || !linkPassword}>連携してログイン</button>
            <button data-control="quiet" data-control-id="Login-lg-link-cancel" className="lg-link-cancel" type="button" disabled={busy} onClick={() => { social.cancelLink(); setLinkPassword(''); }}>キャンセル</button>
          </form>}
          {emailOpen && <div id="lg-email-form" className="lg-email-form">
          <form onSubmit={onSubmit} className="lg-form" key={mode}>
            {mode === 'register' && (
              <label className="lg-field">
                <span className="lg-label">ユーザー名</span>
                <input
                  className="lg-input"
                  type="text"
                  placeholder="例: sakura"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  maxLength={32}
                />
              </label>
            )}
            <label className="lg-field">
              <span className="lg-label">メールアドレス</span>
              <input
                className="lg-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label className="lg-field">
              <span className="lg-label">パスワード</span>
              <div className="lg-password-wrap">
                <input
                  className="lg-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={mode === 'register' ? '6文字以上' : '••••••••'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                <button data-control="icon" data-control-id="Login-lg-eye"
                  type="button"
                  className="lg-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                      <circle cx="12" cy="12" r="3" />
                      <line x1="4" y1="20" x2="20" y2="4" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </label>


            <button data-control="primary" data-control-id="Login-lg-submit" type="submit" className="lg-submit" disabled={busy || loading || (mode === 'register' && !termsAccepted)}>
              {busy ? '処理中…' : mode === 'login' ? 'ログイン' : 'はじめる'}
              {!busy && (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              )}
            </button>
          </form>
          {mode === 'login' && <Link to="/support" className="lg-recovery">パスワードを忘れた方・ログインできない方</Link>}
          </div>}

        <p className="lg-legal">
          <Link to="/terms" className="lg-legal-link">利用規約（EULA）</Link>
          <span className="lg-legal-sep">·</span>
          <a href="/privacy" className="lg-legal-link">プライバシーポリシー</a>
          <span className="lg-legal-sep">·</span>
          {/* Guideline 1.5: サポートへの導線をログイン前からも用意する */}
          <a href="/support" className="lg-legal-link">サポート</a>
        </p>
        </section>
      </div>
    </div>
  );
}

// Vector artwork stays crisp at every size and follows the app's theme colors.
function Kotomaru() {
  return (
    <svg className="lg-mascot" viewBox="0 0 320 290" role="img" aria-label="丸い耳とピンクのほっぺで、手を振ることまる">
      <ellipse className="lg-mascot-shadow" cx="160" cy="256" rx="73" ry="9" fill="var(--primary-dark)" opacity="0.12" />
      <g className="lg-mascot-body">
        <ellipse cx="122" cy="241" rx="21" ry="14" transform="rotate(-12 122 241)" fill="var(--primary)" />
        <ellipse cx="200" cy="241" rx="21" ry="14" transform="rotate(12 200 241)" fill="var(--primary)" />
        <path d="M78 160 C51 156 42 171 49 182 C57 194 74 184 87 177" fill="var(--primary)" />
        <g className="lg-mascot-hand">
          <path d="M239 153 C252 153 254 145 258 134 C263 121 277 127 277 139 C277 159 261 176 241 179" fill="var(--primary)" />
          <path d="M271 111 L274 103 M286 120 L293 116" fill="none" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" />
        </g>
        <g className="lg-mascot-ears">
          <circle cx="103" cy="83" r="29" fill="var(--primary)" />
          <circle cx="217" cy="83" r="29" fill="var(--primary)" />
          <circle cx="103" cy="83" r="17" fill="var(--lg-ear)" />
          <circle cx="217" cy="83" r="17" fill="var(--lg-ear)" />
        </g>
        <path d="M61 166 C61 107 99 75 158 75 C219 75 259 108 259 169 C259 216 222 242 160 242 C99 242 61 216 61 166Z" fill="var(--primary)" />
        <path d="M75 163 C75 112 106 87 159 87 C211 87 244 117 245 165 C245 208 214 232 164 232 C111 232 75 211 75 163Z" fill="var(--primary-light)" opacity="0.28" />
        <path d="M83 176 C83 143 102 120 125 123 Q143 124 160 135 Q177 124 195 123 C218 120 237 145 237 178 C237 210 204 226 160 226 C116 226 83 210 83 176Z" fill="var(--surface)" />
        <g className="lg-mascot-eyes">
          <ellipse cx="133" cy="166" rx="8" ry="11" fill="var(--text)" />
          <ellipse cx="187" cy="166" rx="8" ry="11" fill="var(--text)" />
          <circle cx="130.5" cy="162.5" r="2.6" fill="var(--surface)" />
          <circle cx="184.5" cy="162.5" r="2.6" fill="var(--surface)" />
        </g>
        <g fill="var(--lg-cheek)">
          <ellipse cx="110" cy="184" rx="13" ry="8" />
          <ellipse cx="210" cy="184" rx="13" ry="8" />
        </g>
        <g stroke="var(--lg-cheek-line)" strokeWidth="1.5" strokeLinecap="round" opacity="0.4">
          <path d="M106 182 L105 185 M112 182 L111 185 M207 182 L206 185 M213 182 L212 185" />
        </g>
        <path d="M150 183 Q160 197 170 183" fill="none" stroke="var(--text)" strokeWidth="3.5" strokeLinecap="round" />
      </g>
    </svg>
  );
}

const CSS = `
.lg-page {
  --lg-cheek: #ffe1e4; --lg-cheek-line: #d68a92; --lg-ear: #c8f5e0;
  min-height: 100vh; min-height: 100svh;
  display: flex; align-items: center; justify-content: center;
  padding: 36px 24px calc(32px + env(safe-area-inset-bottom));
  background: var(--surface); color: var(--text); font-family: inherit;
}
.lg-inner { width: 100%; max-width: 360px; }
.lg-hero { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.lg-wordmark { display: flex; align-items: baseline; gap: 3px; font-family: 'Nunito', sans-serif; font-size: 25px; font-weight: 900; font-style: italic; letter-spacing: -1.3px; color: var(--primary-dark); }
.lg-brand-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--primary); }
.lg-tagline { margin: 0; color: var(--text-secondary); font-size: 10px; line-height: 1.6; }
.lg-card { margin-top: 20px; }
.lg-form-heading { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-bottom: 20px; min-height: 126px; }
.lg-heading-copy { flex-shrink: 0; }
.lg-title { margin: 0 0 9px; font-size: 23px; line-height: 1.5; letter-spacing: -.05em; font-weight: 700; }
.lg-form-caption { margin: 0; font-size: 12px; line-height: 1.8; color: var(--text-secondary); }
.lg-mascot { display: block; width: 148px; height: auto; flex-shrink: 0; }
.lg-mascot-hand { transform-origin: 241px 155px; animation: lg-wave 1.8s ease-in-out 2; }
.lg-mascot-eyes { transform-origin: 160px 166px; animation: lg-blink 7s infinite; }
.lg-provider-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.lg-provider, .lg-email-option { display: flex; align-items: center; gap: 12px; min-height: 52px; padding: 10px 15px; background: var(--surface); color: var(--text); border: 1px solid var(--border); border-radius: var(--r-input); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; text-align: left; }
.lg-provider svg, .lg-provider img { flex-shrink: 0; }
.lg-provider img { background: white; border-radius: 50%; object-fit: contain; }
.lg-provider-text { display: flex; flex-direction: column; gap: 3px; font-size: 13px; line-height: 1.4; }
.lg-provider-text small { font-size: 10px; white-space: nowrap; font-weight: 400; color: var(--text-secondary); }
.lg-provider-letter { display: inline-flex; width: 22px; justify-content: center; font-family: Arial, sans-serif; font-weight: 700; }
.lg-provider-letter-facebook { font-size: 25px; color: #1877F2; }
.lg-provider-letter-linkedin { font-size: 19px; color: #0A66C2; }
.lg-email-option { width: 100%; margin-top: 12px; }
.lg-email-option.is-active { border-color: var(--primary-dark); }
.lg-email-chevron { margin-left: auto; font-size: 20px; font-weight: 400; }
.lg-provider:disabled, .lg-email-option:disabled { cursor: wait; opacity: .65; }
.lg-shared-consent { display: flex; flex-direction: column; gap: 14px; margin: 22px 0 0; }
.lg-shared-consent + .lg-error { margin-top: 14px; }
.lg-email-form { margin-top: 22px; }
.lg-recovery { display: block; padding: 16px 0 4px; text-align: center; color: var(--text-secondary); font-size: 12px; text-underline-offset: 3px; }
.lg-link-title { margin: 0; font-size: 15px; color: var(--text); }
.lg-link-cancel { min-height: 44px; border: 0; background: transparent; color: var(--text-secondary); font: inherit; font-size: 12px; cursor: pointer; }
.lg-auth-status { margin: 14px 0 0; font-size: 12px; color: var(--text-secondary); }
.lg-tabs { position: relative; display: flex; margin-bottom: 26px; border-bottom: 1px solid var(--border); }
.lg-tab-indicator { position: absolute; bottom: -1px; left: 0; width: 50%; height: 2px; background: var(--primary-dark); transition: transform .2s ease; }
.lg-tab { position: relative; flex: 1; min-height: 44px; padding: 10px 0 14px; border: none; background: transparent; color: var(--text-secondary); font-size: 13px; font-weight: 500; font-family: inherit; cursor: pointer; }
.lg-tab.is-active { color: var(--text); font-weight: 700; }
.lg-tab:disabled { cursor: default; }
.lg-form { display: flex; flex-direction: column; gap: 20px; }
.lg-field { display: flex; flex-direction: column; gap: 9px; }
.lg-label { font-size: 12px; font-weight: 600; text-align: left; }
.lg-input { width: 100%; min-height: 50px; padding: 13px 14px; border-radius: var(--r-input); border: 1px solid var(--border); background: var(--surface); color: var(--text); font-size: 16px; font-family: inherit; transition: border-color .15s, box-shadow .15s; }
.lg-input::placeholder { color: var(--text-muted); }
.lg-input:focus { border-color: var(--primary-dark); outline: none; box-shadow: 0 0 0 2px var(--primary-ultra); }
.lg-password-wrap { position: relative; }
.lg-password-wrap .lg-input { padding-right: 52px; }
.lg-eye { position: absolute; top: 50%; right: 4px; transform: translateY(-50%); display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border: none; border-radius: var(--r-input); background: transparent; color: var(--text-secondary); cursor: pointer; }
.lg-eye svg { width: 18px; height: 18px; stroke-width: 1.8; }
.lg-error { margin: 0; padding: 12px; border-radius: var(--r-input); background: var(--error-light); color: var(--error); font-size: 12px; line-height: 1.6; }
.lg-terms-check { display: flex; align-items: flex-start; gap: 10px; padding-top: 3px; color: var(--text-secondary); font-size: 11px; line-height: 1.9; text-align: left; cursor: pointer; }
.lg-terms-check input { width: 19px; height: 19px; margin: 2px 0 0; flex-shrink: 0; accent-color: var(--primary-dark); }
.lg-eula-link { color: var(--text); font-weight: 500; text-decoration: underline; text-decoration-color: var(--border); text-underline-offset: 3px; }
.lg-eula { margin: -10px 0 0; font-size: 11px; line-height: 1.8; color: var(--text-secondary); text-align: left; }
.lg-submit { display: flex; align-items: center; justify-content: center; gap: 9px; min-height: 50px; padding: 14px 18px; border-radius: var(--r-input); border: none; background: color-mix(in srgb, var(--primary-dark) 82%, var(--text)); color: var(--surface); font-size: 14px; font-weight: 700; font-family: inherit; cursor: pointer; transition: filter .15s; }
.lg-submit:disabled { background: var(--bg); color: var(--text-secondary); cursor: default; }
.lg-submit:active:not(:disabled) { filter: brightness(.92); }
.lg-legal { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 8px; margin: 25px 0 0; text-align: center; font-size: 10px; line-height: 1.8; }
.lg-legal-link { color: var(--text-secondary); text-decoration: none; padding: 8px 0; }
.lg-legal-sep { color: var(--text-muted); }
.lg-page button:focus-visible, .lg-page a:focus-visible, .lg-terms-check input:focus-visible { outline: 2px solid var(--primary-dark); outline-offset: 4px; }
@keyframes lg-wave { 0%, 60%, 100% { transform: rotate(0); } 68%, 84% { transform: rotate(-12deg); } 76%, 92% { transform: rotate(5deg); } }
@keyframes lg-blink { 0%, 43%, 47%, 100% { transform: scaleY(1); } 45% { transform: scaleY(.1); } }
@media (hover: hover) {
  .lg-provider:hover:not(:disabled), .lg-email-option:hover:not(:disabled) { background: var(--bg); border-color: var(--text-muted); }
  .lg-submit:hover:not(:disabled) { filter: brightness(.93); }
  .lg-tab:hover, .lg-eula-link:hover, .lg-legal-link:hover { color: var(--primary-dark); }
  .lg-eye:hover { background: var(--bg); }
}
@media (max-width: 400px) {
  .lg-page { padding-top: 26px; }
  .lg-title { font-size: 22px; }
  .lg-mascot { width: 130px; }
  .lg-card { margin-top: 20px; }
  .lg-form-heading { min-height: 126px; margin-bottom: 20px; }
}
@media (max-width: 350px) {
  .lg-mascot { width: 108px; }
  .lg-title { font-size: 20px; }
  .lg-form-caption { font-size: 11px; }
  .lg-tagline { font-size: 9px; }
}
@media (prefers-reduced-motion: reduce) {
  .lg-page *, .lg-page *::before, .lg-page *::after { animation: none !important; transition: none !important; }
}
`;
